import { Button, Loading } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { WorkspaceSubscriptionService } from '@affine/core/modules/cloud';
import {
  isDisplayableBillingSubscription,
  isTrialingSubscriptionCurrent,
} from '@affine/core/modules/cloud/utils/subscription-state';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useState } from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import type { SettingState } from '../../types';
import { TeamResumeAction } from './actions';
import { BillingHistory } from './billing-history';
import { PaymentMethodUpdater } from './payment-method';
import { PendingIntervalChangeBanner } from './pending-interval-changes';
import { TeamCard } from './team-card';
import { TrialCard } from './trial-card';

export const WorkspaceSettingBilling = ({
  onChangeSettingState,
}: {
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const workspace = useService(WorkspaceService).workspace;
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();

  const t = useI18n();

  // Resolved plan from feature gate — single source of truth.
  // Correctly handles planOverride (Internal/Beta) unlike Stripe subscriptions.
  const { plan: resolvedPlan } = useFeatureGate();

  const subscriptionService = workspace?.scope.get(
    WorkspaceSubscriptionService
  );
  const subscription = useLiveData(
    subscriptionService?.subscription.subscription$
  );
  const billingState = useLiveData(
    subscriptionService?.subscription.billingState$
  );
  const isRevalidating = useLiveData(
    subscriptionService?.subscription.isRevalidating$
  );

  useEffect(() => {
    subscriptionService?.subscription.revalidate();
  }, [subscriptionService?.subscription]);

  if (workspace === null) {
    return null;
  }

  if (!subscription && isRevalidating && backendAvailable) {
    return <Loading />;
  }

  const effectivePlan = billingState?.effectivePlan ?? resolvedPlan;
  const adminOverride = billingState?.adminOverride ?? null;
  const fallbackPlanAfterOverride =
    billingState?.fallbackPlanAfterOverride ?? null;
  const rawBillingSubscription =
    billingState?.billingSubscription ?? subscription;
  const billingSubscription = isDisplayableBillingSubscription(
    rawBillingSubscription
  )
    ? rawBillingSubscription
    : null;

  // Plans without Stripe subscription (Free, Internal, Beta, admin grants) —
  // show access plan + quotas without pretending there is a paid subscription.
  if (!billingSubscription || billingSubscription.plan === 'Free') {
    return (
      <PlanInfoView
        plan={effectivePlan}
        adminOverride={adminOverride}
        fallbackPlanAfterOverride={fallbackPlanAfterOverride}
        onChangeSettingState={onChangeSettingState}
      />
    );
  }

  // sl-r9ix.3.3: Trialing users see a dedicated trial card (no price, no cancel)
  const isTrialing = isTrialingSubscriptionCurrent(billingSubscription);

  // Paid plans (Pro, Team, Enterprise) — show subscription card with billing info
  return (
    <>
      <SettingHeader
        title={t['com.streamlain.payment.billing-setting.title']()}
        subtitle={t['com.streamlain.payment.billing-setting.subtitle']()}
        action={<SettingsOfflineHeaderAction show={!backendAvailable} />}
      />
      {!backendAvailable && (
        <SettingWrapper title={offlineLabel}>
          <SettingRow name={offlineLabel} desc="" />
        </SettingWrapper>
      )}
      {!isTrialing && <PendingIntervalChangeBanner />}

      <SettingWrapper
        title={t['com.streamlain.payment.billing-setting.information']()}
      >
        {adminOverride && (
          <AccessPlanNotice
            plan={effectivePlan}
            adminOverride={adminOverride}
            fallbackPlanAfterOverride={fallbackPlanAfterOverride}
          />
        )}
        {isTrialing ? (
          <TrialCard onChangeSettingState={onChangeSettingState} />
        ) : (
          <>
            <TeamCard />
            <PaymentMethodUpdater />
            {billingSubscription.end && billingSubscription.canceledAt ? (
              <ResumeSubscription expirationDate={billingSubscription.end} />
            ) : null}
          </>
        )}
      </SettingWrapper>

      <ViewPlansLink onChangeSettingState={onChangeSettingState} />

      {!isTrialing && (
        <SettingWrapper
          title={t['com.streamlain.payment.billing-setting.history']()}
        >
          <BillingHistory />
        </SettingWrapper>
      )}
    </>
  );
};

const planInfoCardStyle: CSSProperties = {
  padding: '20px',
  backgroundColor: 'var(--streamlain-background-secondary-color)',
  borderRadius: '8px',
};

const planInfoQuotaRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 0',
  fontSize: '14px',
  color: 'var(--streamlain-text-primary-color)',
};

const planInfoUpgradeHintStyle: CSSProperties = {
  marginTop: '16px',
  padding: '12px 16px',
  backgroundColor: 'var(--streamlain-background-primary-color)',
  border: '1px solid var(--streamlain-border-color)',
  borderRadius: '8px',
  fontSize: '14px',
  color: 'var(--streamlain-text-secondary-color)',
  lineHeight: '1.5',
};

/**
 * Plan info view — used for plans without active Stripe subscription
 * (Free, Internal, Beta). Shows resolved plan name + quotas.
 * Free plan additionally shows upgrade hint.
 */
const PlanInfoView = ({
  plan,
  adminOverride,
  fallbackPlanAfterOverride,
  onChangeSettingState,
}: {
  plan: string;
  adminOverride?: { plan: string; expiresAt?: string | null } | null;
  fallbackPlanAfterOverride?: string | null;
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const workspaceQuotaService = useService(WorkspaceQuotaService);
  const quota = useLiveData(workspaceQuotaService.quota.quota$);
  const isFree = plan === 'Free';

  useEffect(() => {
    workspaceQuotaService.quota.revalidate();
  }, [workspaceQuotaService]);

  const handleViewPlans = useCallback(() => {
    onChangeSettingState?.({
      activeTab: 'workspace:plans',
      scrollAnchor: 'cloudPricingPlan',
    });
  }, [onChangeSettingState]);

  return (
    <>
      <SettingHeader
        title={t['com.streamlain.payment.billing-setting.title']()}
        subtitle={t['com.streamlain.payment.billing-setting.subtitle']()}
        action={<SettingsOfflineHeaderAction show={!backendAvailable} />}
      />
      <SettingWrapper
        title={t['com.streamlain.payment.billing-setting.information']()}
      >
        <div style={planInfoCardStyle}>
          <div
            style={{
              fontSize: '18px',
              fontWeight: 600,
              color: 'var(--streamlain-text-primary-color)',
              marginBottom: '12px',
            }}
          >
            {t['com.streamlain.payment.billing-setting.plan-card-title']({
              plan,
            })}
          </div>

          {adminOverride && (
            <AccessGrantDetails
              plan={plan}
              adminOverride={adminOverride}
              fallbackPlanAfterOverride={fallbackPlanAfterOverride}
            />
          )}

          {!backendAvailable && !quota ? (
            <div style={planInfoQuotaRowStyle}>
              <span>{offlineLabel}</span>
            </div>
          ) : quota ? (
            <div>
              <div style={planInfoQuotaRowStyle}>
                <span>
                  {t[
                    'com.streamlain.payment.billing-setting.free-plan.quota-members'
                  ]({
                    used: String(quota.memberCount),
                    limit: quota.humanReadable.memberLimit ?? '—',
                  })}
                </span>
              </div>
              <div style={planInfoQuotaRowStyle}>
                <span>
                  {t[
                    'com.streamlain.payment.billing-setting.free-plan.quota-storage'
                  ]({
                    limit: quota.humanReadable.storageQuota ?? '—',
                  })}
                </span>
              </div>
            </div>
          ) : null}

          {isFree && (
            <div style={planInfoUpgradeHintStyle}>
              <p style={{ margin: '0 0 12px 0' }}>
                {t[
                  'com.streamlain.payment.billing-setting.free-plan.upgrade-hint'
                ]()}
              </p>
              <Button variant="primary" onClick={handleViewPlans}>
                {t[
                  'com.streamlain.payment.billing-setting.free-plan.upgrade-button'
                ]()}
              </Button>
            </div>
          )}
        </div>
      </SettingWrapper>
    </>
  );
};

const accessGrantStyle: CSSProperties = {
  marginBottom: '16px',
  padding: '14px 16px',
  backgroundColor: 'var(--streamlain-background-secondary-color)',
  border: '1px solid var(--streamlain-border-color)',
  borderRadius: '8px',
};

const accessGrantTitleStyle: CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
  color: 'var(--streamlain-text-primary-color)',
  marginBottom: '6px',
};

const accessGrantTextStyle: CSSProperties = {
  fontSize: '13px',
  lineHeight: 1.5,
  color: 'var(--streamlain-text-secondary-color)',
};

const formatGrantExpiry = (date: string | null | undefined) =>
  date ? new Date(date).toLocaleDateString() : null;

const AccessGrantDetails = ({
  plan,
  adminOverride,
  fallbackPlanAfterOverride,
}: {
  plan: string;
  adminOverride: { plan: string; expiresAt?: string | null };
  fallbackPlanAfterOverride?: string | null;
}) => {
  const t = useI18n();
  const expiresAt = formatGrantExpiry(adminOverride.expiresAt);
  return (
    <div style={{ ...accessGrantStyle, marginTop: 16 }}>
      <div style={accessGrantTitleStyle}>
        {t['com.streamlain.payment.billing-setting.access-plan.title']({
          plan,
        })}
      </div>
      <div style={accessGrantTextStyle}>
        {expiresAt
          ? t[
              'com.streamlain.payment.billing-setting.access-plan.override-until'
            ]({
              date: expiresAt,
              fallbackPlan: fallbackPlanAfterOverride ?? 'Free',
            })
          : t[
              'com.streamlain.payment.billing-setting.access-plan.override-permanent'
            ]()}
      </div>
    </div>
  );
};

const AccessPlanNotice = ({
  plan,
  adminOverride,
  fallbackPlanAfterOverride,
}: {
  plan: string;
  adminOverride: { plan: string; expiresAt?: string | null };
  fallbackPlanAfterOverride?: string | null;
}) => {
  return (
    <AccessGrantDetails
      plan={plan}
      adminOverride={adminOverride}
      fallbackPlanAfterOverride={fallbackPlanAfterOverride}
    />
  );
};

const ResumeSubscription = ({ expirationDate }: { expirationDate: string }) => {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const handleClick = useCallback(() => {
    setOpen(true);
  }, []);

  return (
    <SettingRow
      name={t['com.streamlain.payment.billing-setting.expiration-date']()}
      desc={t[
        'com.streamlain.payment.billing-setting.expiration-date.description'
      ]({
        expirationDate: new Date(expirationDate).toLocaleDateString(),
      })}
    >
      <TeamResumeAction open={open} onOpenChange={setOpen}>
        <Button onClick={handleClick} variant="primary">
          {t['com.streamlain.payment.billing-setting.resume-subscription']()}
        </Button>
      </TeamResumeAction>
    </SettingRow>
  );
};

const ViewPlansLink = ({
  onChangeSettingState,
}: {
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const t = useI18n();
  const handleClick = useCallback(() => {
    onChangeSettingState?.({ activeTab: 'workspace:plans' });
  }, [onChangeSettingState]);

  return (
    <SettingRow name={t['com.streamlain.payment.view-plans-hint']()} desc="">
      <Button variant="primary" onClick={handleClick}>
        {t['com.streamlain.payment.view-plans']()}
      </Button>
    </SettingRow>
  );
};
